(function () {
  'use strict';

  function debounce(callback, delay) {
    var timeout;
    return function () {
      var context = this;
      var args = arguments;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(function () {
        callback.apply(context, args);
      }, delay);
    };
  }

  function createElement(tag, className, text) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = text;
    return element;
  }

  function initHeader(header) {
    if (!header || header.dataset.pwInitialized === 'true') return;
    header.dataset.pwInitialized = 'true';

    initMobileMenu(header);
    initMobileLevels(header);
    initSubmenus(header);
    initSearchTypewriter(header);
    initPredictiveSearch(header);
    initScrollAwareHeader(header);
  }

  function initSearchTypewriter(header) {
    var search = header.querySelector('[data-pw-search]');
    if (!search) return;

    var input = search.querySelector('[data-pw-search-input]');
    var form = search.querySelector('[data-pw-search-form]');
    var phrasesNode = search.querySelector('[data-pw-search-typewriter]');
    var output = search.querySelector('[data-pw-search-typewriter-output]');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var phrases;
    var target = '';
    var characterIndex = 0;
    var typingTimer = 0;
    var caretTimer = 0;
    var isTyping = false;

    if (!input || !form || !phrasesNode || !output) return;

    try {
      phrases = JSON.parse(phrasesNode.textContent || '[]');
    } catch (error) {
      phrases = [];
    }

    phrases = phrases.filter(function (phrase) {
      return typeof phrase === 'string' && phrase.trim().length > 0;
    });
    if (!phrases.length) return;

    target = phrases[Math.floor(Math.random() * phrases.length)];

    function clearTimers() {
      window.clearTimeout(typingTimer);
      window.clearTimeout(caretTimer);
    }

    function hideCaret() {
      form.classList.add('is-typewriter-caret-hidden');
    }

    function finishImmediately() {
      clearTimers();
      output.textContent = target;
      if (isTyping) {
        isTyping = false;
      }
      hideCaret();
    }

    function typeNextCharacter() {
      if (!isTyping || input.value || document.hidden) {
        finishImmediately();
        return;
      }

      characterIndex += 1;
      output.textContent = target.slice(0, characterIndex);

      if (characterIndex < target.length) {
        typingTimer = window.setTimeout(typeNextCharacter, 38);
        return;
      }

      isTyping = false;
      caretTimer = window.setTimeout(hideCaret, 4200);
    }

    input.setAttribute('placeholder', '');
    output.textContent = reduceMotion ? target : '';
    form.classList.add('is-typewriter-active');
    form.classList.toggle('is-typewriter-has-value', Boolean(input.value));

    if (reduceMotion || input.value) {
      hideCaret();
    } else {
      isTyping = true;
      typingTimer = window.setTimeout(typeNextCharacter, 180);
    }

    input.addEventListener('focus', finishImmediately);
    input.addEventListener('pointerdown', finishImmediately);
    input.addEventListener('input', function () {
      if (input.value) finishImmediately();
      output.textContent = target;
      form.classList.toggle('is-typewriter-has-value', Boolean(input.value));
      hideCaret();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) finishImmediately();
    });
  }

  function initScrollAwareHeader(header) {
    var stickyEnabled = header.classList.contains('pw-header--sticky');
    var topContainer = header.querySelector('.pw-header__container--top');
    var topRow = header.querySelector('.pw-header__top');
    var bottomRow = header.querySelector('.pw-header__bottom');
    var mobileSearchButton = header.querySelector('[data-pw-mobile-search-open]');
    var search = header.querySelector('[data-pw-search]');
    var searchInput = header.querySelector('[data-pw-search-input]');
    var searchResults = header.querySelector('[data-pw-search-results]');
    var mobileMedia = window.matchMedia('(max-width: 1023px)');
    var lastScrollY = Math.max(window.scrollY, 0);
    var direction = 0;
    var directionDistance = 0;
    var ticking = false;
    var desktopStickyStart = 0;
    var wasMobile = mobileMedia.matches;
    var hideOffset = 120;
    var hideTolerance = 36;
    var showTolerance = 14;

    if (!stickyEnabled || !topContainer || !topRow) return;

    function setMeasuredHeights() {
      var topHeight = topContainer.offsetHeight;
      var bottomHeight = bottomRow ? bottomRow.offsetHeight : 0;

      if (!header.classList.contains('is-mobile-scrolled')) {
        header.style.setProperty('--pw-mobile-full-height', (topHeight + bottomHeight) + 'px');
      }
      header.style.setProperty('--pw-desktop-full-height', (topHeight + bottomHeight) + 'px');
      desktopStickyStart = header.getBoundingClientRect().top + window.scrollY + topHeight;
    }

    function setSearchExpanded(expanded, focusInput) {
      header.classList.toggle('is-mobile-search-expanded', expanded);
      if (mobileSearchButton) mobileSearchButton.setAttribute('aria-expanded', String(expanded));
      if (expanded && focusInput && searchInput) {
        window.requestAnimationFrame(function () { searchInput.focus(); });
      }
    }

    function interactionIsOpen() {
      var menuOpen = document.body.classList.contains('pw-menu-open');
      var submenuOpen = Boolean(header.querySelector('[data-pw-nav-item].is-open'));
      var resultsOpen = searchResults && !searchResults.hidden;
      var searchFocused = searchInput && document.activeElement === searchInput;
      return menuOpen || submenuOpen || resultsOpen || searchFocused;
    }

    function resetMobileState() {
      header.classList.remove('is-mobile-scrolled', 'is-mobile-compact', 'is-mobile-hidden');
      setSearchExpanded(false, false);
    }

    function resetDesktopState() {
      header.classList.remove('is-desktop-nav-sticky');
    }

    function updateScrollState() {
      ticking = false;
      var currentScrollY = Math.max(window.scrollY, 0);
      var delta = currentScrollY - lastScrollY;

      if (!mobileMedia.matches) {
        resetMobileState();
        header.classList.toggle('is-desktop-nav-sticky', currentScrollY >= desktopStickyStart);
        lastScrollY = currentScrollY;
        return;
      }

      resetDesktopState();

      if (currentScrollY <= 8) {
        resetMobileState();
        direction = 0;
        directionDistance = 0;
        lastScrollY = currentScrollY;
        setMeasuredHeights();
        return;
      }

      if (interactionIsOpen()) {
        header.classList.add('is-mobile-scrolled', 'is-mobile-compact');
        header.classList.remove('is-mobile-hidden');
        lastScrollY = currentScrollY;
        return;
      }

      if (Math.abs(delta) < 2) {
        lastScrollY = currentScrollY;
        return;
      }

      var nextDirection = delta > 0 ? 1 : -1;
      if (nextDirection !== direction) {
        direction = nextDirection;
        directionDistance = 0;
      }
      directionDistance += Math.abs(delta);

      if (nextDirection > 0 && currentScrollY > hideOffset && directionDistance >= hideTolerance) {
        header.classList.add('is-mobile-scrolled', 'is-mobile-hidden');
        header.classList.remove('is-mobile-compact');
        setSearchExpanded(false, false);
        directionDistance = 0;
      } else if (nextDirection < 0 && directionDistance >= showTolerance) {
        header.classList.add('is-mobile-scrolled', 'is-mobile-compact');
        header.classList.remove('is-mobile-hidden');
        directionDistance = 0;
      }

      lastScrollY = currentScrollY;
    }

    function requestScrollUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateScrollState);
    }

    if (mobileSearchButton && searchInput) {
      mobileSearchButton.addEventListener('click', function () {
        header.classList.add('is-mobile-scrolled', 'is-mobile-compact');
        header.classList.remove('is-mobile-hidden');
        setSearchExpanded(true, true);
      });
    }

    document.addEventListener('click', function (event) {
      if (!header.classList.contains('is-mobile-search-expanded')) return;
      if ((search && search.contains(event.target)) || (mobileSearchButton && mobileSearchButton.contains(event.target))) return;
      setSearchExpanded(false, false);
    });

    header.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && header.classList.contains('is-mobile-search-expanded')) {
        setSearchExpanded(false, false);
        if (mobileSearchButton) mobileSearchButton.focus();
      }
    });

    setMeasuredHeights();
    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', debounce(function () {
      var isMobile = mobileMedia.matches;

      if (isMobile !== wasMobile) {
        resetMobileState();
        resetDesktopState();
        wasMobile = isMobile;
        setMeasuredHeights();
      } else if (!isMobile) {
        setMeasuredHeights();
      } else if (window.scrollY <= 8) {
        resetMobileState();
        setMeasuredHeights();
      }

      lastScrollY = Math.max(window.scrollY, 0);
      requestScrollUpdate();
    }, 100), { passive: true });
    requestScrollUpdate();
  }

  function initMobileMenu(header) {
    var openButton = header.querySelector('[data-pw-mobile-open]');
    var drawer = header.querySelector('[data-pw-mobile-menu]');
    var closeButton = header.querySelector('[data-pw-mobile-close]');
    var overlay = header.querySelector('[data-pw-mobile-overlay]');
    var lastFocusedElement = null;

    if (!openButton || !drawer || !closeButton || !overlay) return;

    function openMenu() {
      lastFocusedElement = document.activeElement;
      drawer.hidden = false;
      overlay.hidden = false;
      openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('pw-menu-open');
      closeButton.focus();
    }

    function closeMenu(restoreFocus) {
      drawer.querySelectorAll('[data-pw-mobile-level]').forEach(function (level) {
        level.classList.remove('is-active');
        level.hidden = true;
      });
      drawer.querySelectorAll('[data-pw-mobile-level-open]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });
      drawer.hidden = true;
      overlay.hidden = true;
      openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('pw-menu-open');
      if (restoreFocus && lastFocusedElement) lastFocusedElement.focus();
    }

    openButton.addEventListener('click', openMenu);
    closeButton.addEventListener('click', function () { closeMenu(true); });
    overlay.addEventListener('click', function () { closeMenu(true); });

    drawer.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu(true);
      }
    });

    window.addEventListener('resize', function () {
      if (window.matchMedia('(min-width: 1024px)').matches && !drawer.hidden) {
        closeMenu(false);
      }
    }, { passive: true });
  }

  function initMobileLevels(header) {
    var navigation = header.querySelector('[data-pw-mobile-navigation]');
    if (!navigation) return;

    navigation.addEventListener('click', function (event) {
      var openButton = event.target.closest('[data-pw-mobile-level-open]');
      var backButton = event.target.closest('[data-pw-mobile-level-back]');

      if (openButton) {
        var panelId = openButton.getAttribute('aria-controls');
        var panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        openButton.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        panel.scrollTop = 0;
        window.requestAnimationFrame(function () {
          panel.classList.add('is-active');
          var back = panel.querySelector(':scope > .pw-mobile-nav__level-header [data-pw-mobile-level-back]');
          if (back) back.focus();
        });
        return;
      }

      if (backButton) {
        var currentPanel = backButton.closest('[data-pw-mobile-level]');
        if (!currentPanel) return;
        var controllingButton = navigation.querySelector('[aria-controls="' + currentPanel.id + '"]');

        currentPanel.classList.remove('is-active');
        if (controllingButton) controllingButton.setAttribute('aria-expanded', 'false');
        window.setTimeout(function () {
          currentPanel.hidden = true;
          if (controllingButton) controllingButton.focus();
        }, 180);
      }
    });
  }

  function initSubmenus(header) {
    var toggles = header.querySelectorAll('[data-pw-submenu-toggle]');
    var preciseHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    var hoverOpenDelay = 350;
    var hoverCloseDelay = 400;

    function clearHoverTimers(item) {
      if (!item) return;
      window.clearTimeout(item.pwHoverOpenTimer);
      window.clearTimeout(item.pwHoverCloseTimer);
      item.pwHoverOpenTimer = null;
      item.pwHoverCloseTimer = null;
    }

    function closeItem(item) {
      if (!item) return;
      clearHoverTimers(item);
      var toggle = item.querySelector(':scope > .pw-nav__top-row [data-pw-submenu-toggle]');
      var panel = item.querySelector(':scope > [data-pw-submenu-panel]');
      item.classList.remove('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (panel && item.closest('.pw-nav--sidebar')) panel.hidden = true;
    }

    function openItem(item, toggle, panel, navigation) {
      if (!item || !toggle || !panel || !navigation) return;

      clearHoverTimers(item);
      navigation.querySelectorAll(':scope > .pw-nav__list > [data-pw-nav-item].is-open').forEach(function (openItemElement) {
        if (openItemElement !== item) closeItem(openItemElement);
      });

      item.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      if (navigation.dataset.pwNavigation === 'sidebar') panel.hidden = false;
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var item = toggle.closest('[data-pw-nav-item]');
        var panelId = toggle.getAttribute('aria-controls');
        var panel = panelId ? document.getElementById(panelId) : null;
        var navigation = toggle.closest('[data-pw-navigation]');
        var willOpen = toggle.getAttribute('aria-expanded') !== 'true';

        if (!item || !panel || !navigation) return;

        clearHoverTimers(item);
        if (willOpen) {
          openItem(item, toggle, panel, navigation);
        } else {
          closeItem(item);
        }
      });
    });

    header.querySelectorAll('.pw-nav--classic [data-pw-nav-item].pw-nav__item--has-panel').forEach(function (item) {
      var toggle = item.querySelector(':scope > .pw-nav__top-row [data-pw-submenu-toggle]');
      var panel = item.querySelector(':scope > [data-pw-submenu-panel]');
      var navigation = item.closest('[data-pw-navigation]');

      if (!toggle || !panel || !navigation) return;

      item.addEventListener('pointerenter', function (event) {
        if (!preciseHover.matches || event.pointerType === 'touch') return;
        window.clearTimeout(item.pwHoverCloseTimer);
        window.clearTimeout(item.pwHoverOpenTimer);
        item.pwHoverOpenTimer = window.setTimeout(function () {
          openItem(item, toggle, panel, navigation);
        }, hoverOpenDelay);
      });

      item.addEventListener('pointerleave', function (event) {
        if (!preciseHover.matches || event.pointerType === 'touch') return;
        window.clearTimeout(item.pwHoverOpenTimer);
        window.clearTimeout(item.pwHoverCloseTimer);
        item.pwHoverCloseTimer = window.setTimeout(function () {
          closeItem(item);
        }, hoverCloseDelay);
      });
    });

    document.addEventListener('click', function (event) {
      if (header.contains(event.target)) return;
      header.querySelectorAll('.pw-nav--classic [data-pw-nav-item].is-open').forEach(closeItem);
    });

    header.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openItem = event.target.closest('[data-pw-nav-item].is-open');
      if (!openItem) return;
      var toggle = openItem.querySelector(':scope > .pw-nav__top-row [data-pw-submenu-toggle]');
      closeItem(openItem);
      if (toggle) toggle.focus();
    });
  }

  function initPredictiveSearch(header) {
    var search = header.querySelector('[data-pw-search]');
    if (!search) return;

    var form = search.querySelector('[data-pw-search-form]');
    var input = search.querySelector('[data-pw-search-input]');
    var results = search.querySelector('[data-pw-search-results]');
    var correctionTermsNode = search.querySelector('[data-pw-search-correction-terms]');
    var endpoint = search.dataset.predictiveUrl;
    var didYouMeanLabel = search.dataset.didYouMeanLabel || localizedText('Bedoel je', 'Did you mean', 'Vouliez-vous dire', 'Meintest du');
    var recentSearchesLabel = search.dataset.recentSearchesLabel || localizedText('Recent gezocht', 'Recently searched', 'Recherches récentes', 'Zuletzt gesucht');
    var currency = search.dataset.currency || 'EUR';
    var typeLabels = {
      product: search.dataset.labelProduct || 'Product',
      collection: search.dataset.labelCollection || 'Collection',
      page: search.dataset.labelPage || 'Page',
      article: search.dataset.labelArticle || 'Blog'
    };
    var correctionTerms = parseCorrectionTerms(correctionTermsNode);
    var historyStorageKey = 'pw-search-history-v1';
    var historyLimit = 3;
    var activeRequest = null;
    var requestSequence = 0;

    if (!form || !input || !results || !endpoint || !window.fetch) return;

    function closeResults() {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    }

    function openResults() {
      if (!results.childElementCount) return;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function localizedText(dutch, english, french, german) {
      var locale = String(document.documentElement.lang || 'nl').toLowerCase();
      if (locale.indexOf('fr') === 0) return french;
      if (locale.indexOf('de') === 0) return german;
      if (locale.indexOf('en') === 0) return english;
      return dutch;
    }

    function createAllResultsLink(query) {
      var allResultsText = localizedText(
        "Bekijk alle resultaten voor ‘" + query + "’ →",
        "View all results for ‘" + query + "’ →",
        "Voir tous les résultats pour « " + query + " » →",
        "Alle Ergebnisse für „" + query + "“ ansehen →"
      );
      var allResults = createElement('a', 'pw-search__all-results', allResultsText);
      var searchUrl = new URL(form.action, window.location.origin);
      searchUrl.searchParams.set('q', query);
      searchUrl.searchParams.set('options[prefix]', 'last');
      allResults.href = searchUrl.toString();
      return allResults;
    }

    function renderSearchFallback(query) {
      if (!query || input.value.trim() !== query) return;
      results.replaceChildren();
      appendSearchHistory(results);
      results.appendChild(createAllResultsLink(query));
      openResults();
    }

    function cleanSearchTerm(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    function getRecentSearches() {
      try {
        var saved = JSON.parse(window.localStorage.getItem(historyStorageKey) || '[]');
        if (!Array.isArray(saved)) return [];

        var seen = {};
        return saved
          .map(cleanSearchTerm)
          .filter(function (term) {
            var normalized = normalizeTerm(term);
            if (term.length < 2 || term.length > 80 || !normalized || seen[normalized]) return false;
            seen[normalized] = true;
            return true;
          })
          .slice(0, historyLimit);
      } catch (error) {
        return [];
      }
    }

    function saveRecentSearch(query) {
      var term = cleanSearchTerm(query);
      if (term.length < 2 || term.length > 80) return;

      try {
        var normalized = normalizeTerm(term);
        var updated = [term].concat(getRecentSearches().filter(function (item) {
          return normalizeTerm(item) !== normalized;
        }));
        window.localStorage.setItem(historyStorageKey, JSON.stringify(updated.slice(0, historyLimit)));
      } catch (error) {
      }
    }

    function appendSearchHistory(container) {
      var history = getRecentSearches();
      if (!history.length) return false;

      var section = createElement('div', 'pw-search__history');
      var label = createElement('span', 'pw-search__history-label', recentSearchesLabel);
      var list = createElement('div', 'pw-search__history-list');

      history.forEach(function (term) {
        var button = createElement('button', 'pw-search__history-item', term);
        button.type = 'button';
        button.addEventListener('click', function () {
          input.value = term;
          input.focus();
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
        list.appendChild(button);
      });

      section.appendChild(label);
      section.appendChild(list);
      container.appendChild(section);
      return true;
    }

    function renderRecentSearches() {
      results.replaceChildren();
      if (!appendSearchHistory(results)) {
        closeResults();
        return;
      }
      openResults();
    }

    function requestIsCurrent(sequence, query) {
      return sequence === requestSequence && input.value.trim() === query;
    }

    function waitBeforeRetry(delay, controller) {
      return new Promise(function (resolve, reject) {
        window.setTimeout(function () {
          if (controller.signal.aborted) {
            var abortError = new Error('Predictive search aborted');
            abortError.name = 'AbortError';
            reject(abortError);
            return;
          }
          resolve();
        }, delay);
      });
    }

    function fetchPredictivePayload(url, controller, attempt) {
      return fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) {
            var responseError = new Error('Predictive search failed: ' + response.status);
            responseError.status = response.status;
            throw responseError;
          }
          return response.json();
        })
        .catch(function (error) {
          var status = Number(error.status || 0);
          var isTemporaryHttpError = status === 429 || status >= 500;
          var isTemporaryNetworkError = error instanceof TypeError;
          var canRetry = error.name !== 'AbortError'
            && attempt < 1
            && (isTemporaryHttpError || isTemporaryNetworkError);

          if (!canRetry) throw error;

          return waitBeforeRetry(260, controller).then(function () {
            return fetchPredictivePayload(url, controller, attempt + 1);
          });
        });
    }

    function formatPrice(value) {
      var number = Number(value);
      if (!Number.isFinite(number)) return '';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
          style: 'currency',
          currency: currency
        }).format(number);
      } catch (error) {
        return number.toFixed(2);
      }
    }

    function sizedImageUrl(source) {
      if (!source) return '';
      try {
        var url = new URL(source, window.location.origin);
        url.searchParams.set('width', '96');
        return url.toString();
      } catch (error) {
        return source;
      }
    }

    function parseCorrectionTerms(node) {
      if (!node) return [];

      try {
        var value = JSON.parse(node.textContent || '""');
        var seen = {};
        return String(value || '')
          .split(/[\n,;]+/)
          .map(function (term) { return term.trim(); })
          .filter(function (term) {
            var normalized = normalizeTerm(term);
            if (!normalized || seen[normalized]) return false;
            seen[normalized] = true;
            return true;
          });
      } catch (error) {
        return [];
      }
    }

    function normalizeTerm(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    function editDistance(source, target) {
      var sourceLength = source.length;
      var targetLength = target.length;
      var matrix = [];
      var row;
      var column;

      for (row = 0; row <= sourceLength; row += 1) {
        matrix[row] = [row];
      }
      for (column = 0; column <= targetLength; column += 1) {
        matrix[0][column] = column;
      }

      for (row = 1; row <= sourceLength; row += 1) {
        for (column = 1; column <= targetLength; column += 1) {
          var substitutionCost = source.charAt(row - 1) === target.charAt(column - 1) ? 0 : 1;
          var distance = Math.min(
            matrix[row - 1][column] + 1,
            matrix[row][column - 1] + 1,
            matrix[row - 1][column - 1] + substitutionCost
          );

          if (
            row > 1
            && column > 1
            && source.charAt(row - 1) === target.charAt(column - 2)
            && source.charAt(row - 2) === target.charAt(column - 1)
          ) {
            distance = Math.min(distance, matrix[row - 2][column - 2] + 1);
          }

          matrix[row][column] = distance;
        }
      }

      return matrix[sourceLength][targetLength];
    }

    function bestListCorrection(value, candidates) {
      var normalizedValue = normalizeTerm(value);
      var maximumDistance = normalizedValue.length >= 8 ? 2 : 1;
      var best = '';
      var bestDistance = maximumDistance + 1;
      var hasTie = false;

      if (normalizedValue.length < 5) return '';

      candidates.forEach(function (candidate) {
        var normalizedCandidate = normalizeTerm(candidate);
        if (!normalizedCandidate || normalizedCandidate === normalizedValue) return;
        if (normalizedCandidate.indexOf(normalizedValue) === 0) return;
        if (Math.abs(normalizedCandidate.length - normalizedValue.length) > maximumDistance) return;

        var distance = editDistance(normalizedValue, normalizedCandidate);
        if (distance > maximumDistance) return;

        if (distance < bestDistance) {
          best = candidate;
          bestDistance = distance;
          hasTie = false;
        } else if (distance === bestDistance && normalizeTerm(best) !== normalizedCandidate) {
          hasTie = true;
        }
      });

      return hasTie ? '' : best;
    }

    function findListCorrection(query) {
      var correction = bestListCorrection(query, correctionTerms);
      if (correction) return correction;

      var words = cleanSearchTerm(query).split(/\s+/);
      var singleWordTerms = correctionTerms.filter(function (term) {
        return normalizeTerm(term).indexOf(' ') === -1;
      });

      for (var index = 0; index < words.length; index += 1) {
        var correctedWord = bestListCorrection(words[index], singleWordTerms);
        if (correctedWord) {
          var correctedWords = words.slice();
          correctedWords[index] = correctedWord;
          return correctedWords.join(' ');
        }
      }

      return '';
    }

    function getResourceItems(payload, resourceType) {
      var resources = payload && payload.resources && payload.resources.results;
      return resources && Array.isArray(resources[resourceType]) ? resources[resourceType] : [];
    }

    function getPrimaryProductText(product) {
      return normalizeTerm([
        product && product.vendor,
        product && product.title,
        product && product.type
      ].filter(Boolean).join(' '));
    }

    function getExpandedProductTexts(product) {
      var vendor = product && product.vendor;
      var values = [
        product && product.title,
        product && product.type,
        product && product.body
      ].filter(Boolean);
      var productTexts = [];

      if (product && Array.isArray(product.tags)) {
        values = values.concat(product.tags);
      }

      if (product && Array.isArray(product.variants)) {
        product.variants.forEach(function (variant) {
          if (variant && variant.title) values.push(variant.title);
        });
      }

      productTexts.push(normalizeTerm([vendor].concat(values).filter(Boolean).join(' ')));
      values.forEach(function (value) {
        productTexts.push(normalizeTerm([vendor, value].filter(Boolean).join(' ')));
      });

      return productTexts.filter(Boolean);
    }

    function productHasExactPhrase(product, query, expandedFields) {
      var normalizedQuery = normalizeTerm(query);
      var productTexts = expandedFields
        ? getExpandedProductTexts(product)
        : [getPrimaryProductText(product)];

      return Boolean(normalizedQuery && productTexts.some(function (productText) {
        return productText.indexOf(normalizedQuery) !== -1;
      }));
    }

    function getProductKey(product) {
      if (!product) return '';
      return String(product.id || product.handle || product.url || product.title || '');
    }

    function shouldRequestExpandedProductMatch(payload, query) {
      var normalizedQuery = normalizeTerm(query);
      var words = normalizedQuery.split(' ').filter(Boolean);
      var products = getResourceItems(payload, 'products');

      if (normalizedQuery.length < 10 || words.length < 2 || words[words.length - 1].length < 4) return false;

      return !products.some(function (product) {
        return productHasExactPhrase(product, query, false);
      });
    }

    function mergeExactExpandedProducts(primaryPayload, expandedPayload, query) {
      var products = getResourceItems(primaryPayload, 'products').slice(0, 10);
      var exactMatches = [];
      var exactMatchKeys = {};

      if (!expandedPayload) return products;

      getResourceItems(expandedPayload, 'products').forEach(function (product) {
        var key = getProductKey(product);
        if (!key || exactMatchKeys[key] || !productHasExactPhrase(product, query, true)) return;
        exactMatchKeys[key] = true;
        exactMatches.push(product);
      });

      if (!exactMatches.length) return products;

      return exactMatches.concat(products.filter(function (product) {
        return !exactMatchKeys[getProductKey(product)];
      })).slice(0, 10);
    }

    function getCollectionFallbackQuery(payload, query) {
      var normalizedQuery = normalizeTerm(query);
      var firstProduct = getResourceItems(payload, 'products')[0];
      var vendor = cleanSearchTerm(firstProduct && firstProduct.vendor);
      var normalizedVendor = normalizeTerm(vendor);

      if (getResourceItems(payload, 'collections').length || !normalizedVendor) return '';
      if (normalizedVendor.split(' ').length < 2 || normalizedVendor === normalizedQuery) return '';
      if (normalizedQuery.indexOf(normalizedVendor) !== 0) return '';
      return vendor;
    }

    function collectionMatchesQuery(collection, query) {
      var collectionTitle = normalizeTerm(collection && collection.title);
      var normalizedQuery = normalizeTerm(query);

      return Boolean(
        collectionTitle
        && normalizedQuery
        && (collectionTitle === normalizedQuery || collectionTitle.indexOf(normalizedQuery) !== -1 || normalizedQuery.indexOf(collectionTitle) !== -1)
      );
    }

    function mergeCollectionResults(primaryPayload, fallbackPayload, fallbackQuery) {
      var primaryCollections = getResourceItems(primaryPayload, 'collections').slice(0, 1);
      if (primaryCollections.length || !fallbackPayload || !fallbackQuery) return primaryCollections;

      return getResourceItems(fallbackPayload, 'collections').filter(function (collection) {
        return collectionMatchesQuery(collection, fallbackQuery);
      }).slice(0, 1);
    }

    function createPredictiveUrl(query, expandedProductFields) {
      var url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', expandedProductFields ? 'product' : 'product,collection');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'show');

      if (expandedProductFields) {
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,body,tag');
      }

      return url.toString();
    }

    function createCollectionPredictiveUrl(query) {
      var url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'collection');
      url.searchParams.set('resources[limit]', '1');
      url.searchParams.set('resources[limit_scope]', 'each');
      return url.toString();
    }

    function fetchOptionalPayload(url, controller) {
      return fetchPredictivePayload(url, controller, 0).catch(function (error) {
        if (error.name === 'AbortError') throw error;
        return null;
      });
    }

    function fetchSupplementaryResults(payload, query, controller) {
      var collectionFallbackQuery = getCollectionFallbackQuery(payload, query);
      var expandedProducts = shouldRequestExpandedProductMatch(payload, query)
        ? fetchOptionalPayload(createPredictiveUrl(query, true), controller)
        : Promise.resolve(null);
      var fallbackCollection = collectionFallbackQuery
        ? fetchOptionalPayload(createCollectionPredictiveUrl(collectionFallbackQuery), controller)
        : Promise.resolve(null);

      return Promise.all([expandedProducts, fallbackCollection]).then(function (responses) {
        return {
          expandedPayload: responses[0],
          collectionFallbackPayload: responses[1],
          collectionFallbackQuery: collectionFallbackQuery
        };
      });
    }

    function appendResult(list, item, kind) {
      var listItem = createElement('li');
      var link = createElement('a', 'pw-search__result-link');
      var imageSource = item.image || (item.featured_image && item.featured_image.url) || '';
      var image;

      link.href = item.url;

      if (imageSource) {
        image = createElement('img', 'pw-search__result-image');
        image.src = sizedImageUrl(imageSource);
        image.alt = '';
        image.width = 48;
        image.height = 48;
        image.loading = 'lazy';
      } else {
        image = createElement('span', 'pw-search__result-placeholder');
        image.setAttribute('aria-hidden', 'true');
        image.textContent = typeLabels[kind].slice(0, 1).toUpperCase();
      }

      var content = createElement('span', 'pw-search__result-content');
      var type = createElement('span', 'pw-search__result-type', typeLabels[kind]);
      var title = createElement('span', 'pw-search__result-title', item.title || item.text || '');
      content.appendChild(type);
      content.appendChild(title);

      link.appendChild(image);
      link.appendChild(content);

      if (kind === 'product') {
        var currentPrice = Number(item.price);
        var comparePrice = Number(item.compare_at_price_min);
        if (!(comparePrice > currentPrice)) comparePrice = Number(item.compare_at_price_max);

        var priceGroup = createElement('span', 'pw-search__result-prices');
        var priceClass = comparePrice > currentPrice
          ? 'pw-search__result-price pw-search__result-price--sale'
          : 'pw-search__result-price';
        priceGroup.appendChild(createElement('span', priceClass, formatPrice(currentPrice)));

        if (comparePrice > currentPrice) {
          var compare = createElement('s', 'pw-search__result-compare-price', formatPrice(comparePrice));
          priceGroup.appendChild(compare);
        }

        link.appendChild(priceGroup);
      }

      listItem.appendChild(link);
      list.appendChild(listItem);
    }

    function appendCorrection(container, correction) {
      var correctionItem = createElement('div', 'pw-search__correction-item');
      var button = createElement('button', 'pw-search__correction');
      var label = createElement('span', 'pw-search__correction-label', didYouMeanLabel + ':');
      var term = createElement('span', 'pw-search__correction-term', correction + '?');

      button.type = 'button';
      button.setAttribute('aria-label', didYouMeanLabel + ': ' + correction);
      button.appendChild(label);
      button.appendChild(term);
      button.addEventListener('click', function () {
        input.value = correction;
        input.focus();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });

      correctionItem.appendChild(button);
      container.appendChild(correctionItem);
    }

    function renderResults(payload, query, expandedPayload, collectionFallbackPayload, collectionFallbackQuery, correction) {
      var products = mergeExactExpandedProducts(payload, expandedPayload, query);
      var collections = mergeCollectionResults(payload, collectionFallbackPayload, collectionFallbackQuery);

      results.replaceChildren();

      if (!products.length && !collections.length) {
        renderSearchFallback(query);
        return;
      }

      if (correction) appendCorrection(results, correction);

      if (products.length) {
        var productList = createElement('ul', 'pw-search__result-list');
        products.forEach(function (item) { appendResult(productList, item, 'product'); });
        results.appendChild(productList);
      }

      if (collections.length) {
        var collectionList = createElement('ul', 'pw-search__secondary-list');
        collections.forEach(function (item) { appendResult(collectionList, item, 'collection'); });
        results.appendChild(collectionList);
      }

      appendSearchHistory(results);
      results.appendChild(createAllResultsLink(query));
      openResults();
    }

    var requestResults = debounce(function () {
      var query = input.value.trim();
      var currentSequence = ++requestSequence;

      if (query.length < 2) {
        if (activeRequest) activeRequest.abort();
        activeRequest = null;
        input.setAttribute('aria-busy', 'false');
        results.replaceChildren();
        if (!query) {
          renderRecentSearches();
        } else {
          closeResults();
        }
        return;
      }

      if (activeRequest) activeRequest.abort();
      var controller = new AbortController();
      activeRequest = controller;
      input.setAttribute('aria-busy', 'true');

      var listCorrection = findListCorrection(query);

      fetchPredictivePayload(createPredictiveUrl(query, false), controller, 0)
        .then(function (payload) {
          if (!requestIsCurrent(currentSequence, query)) return null;

          return fetchSupplementaryResults(payload, query, controller)
            .then(function (supplementaryResults) {
              return {
                payload: payload,
                expandedPayload: supplementaryResults.expandedPayload,
                collectionFallbackPayload: supplementaryResults.collectionFallbackPayload,
                collectionFallbackQuery: supplementaryResults.collectionFallbackQuery,
                correction: listCorrection
              };
            });
        })
        .then(function (searchResult) {
          if (!searchResult || !requestIsCurrent(currentSequence, query)) return;
          activeRequest = null;
          input.setAttribute('aria-busy', 'false');
          if (!searchResult.payload) {
            renderSearchFallback(query);
            return;
          }
          renderResults(
            searchResult.payload,
            query,
            searchResult.expandedPayload,
            searchResult.collectionFallbackPayload,
            searchResult.collectionFallbackQuery,
            searchResult.correction
          );
        })
        .catch(function (error) {
          if (error.name === 'AbortError' || !requestIsCurrent(currentSequence, query)) return;
          activeRequest = null;
          input.setAttribute('aria-busy', 'false');
          renderSearchFallback(query);
        });
    }, 220);

    input.addEventListener('input', requestResults);
    input.addEventListener('focus', function () {
      if (!input.value.trim()) {
        renderRecentSearches();
      } else if (results.childElementCount) {
        openResults();
      }
    });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeResults();
    });

    document.addEventListener('click', function (event) {
      if (!search.contains(event.target)) closeResults();
    });

    form.addEventListener('submit', function () {
      saveRecentSearch(input.value);
    });

    results.addEventListener('click', function (event) {
      var resultLink = event.target.closest('a.pw-search__result-link, a.pw-search__all-results');
      if (resultLink) saveRecentSearch(input.value);
    });
  }

  function initWithin(root) {
    if (root.matches && root.matches('[data-pw-header]')) initHeader(root);
    root.querySelectorAll('[data-pw-header]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initWithin(document); });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initWithin(event.target);
  });
}());
